import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Plus, Edit, Trash2, Music, Play, Square, MoveUp, MoveDown } from 'lucide-react';
import { toast } from 'sonner';

const PlaylistManagement = () => {
  const [playlist, setPlaylist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ title: '', file: null });
  const [audioTest, setAudioTest] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadPlaylist();
  }, []);

  const loadPlaylist = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/playlist/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaylist(response.data);
    } catch (error) {
      toast.error('Erro ao carregar playlist');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');

      if (editing) {
        // Edição - apenas título e ativo
        await axios.put(`${BACKEND_URL}/api/playlist/${editing.id}`, {
          title: formData.title,
          active: formData.active
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Música atualizada!');
      } else {
        // Criação - upload de arquivo
        if (!formData.file) {
          toast.error('Selecione um arquivo MP3');
          return;
        }

        const uploadData = new FormData();
        uploadData.append('title', formData.title);
        uploadData.append('file', formData.file);

        await axios.post(`${BACKEND_URL}/api/playlist/`, uploadData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Música adicionada!');
      }
      setDialogOpen(false);
      setFormData({ title: '', file: null });
      loadPlaylist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar música');
    }
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      await axios.delete(`${BACKEND_URL}/api/playlist/${deletingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Música removida!');
      setShowDeleteConfirm(false);
      setDeletingId(null);
      loadPlaylist();
    } catch (error) {
      toast.error('Erro ao remover música');
      setShowDeleteConfirm(false);
    }
  };

  const handleMove = async (index, direction) => {
    const newPlaylist = [...playlist];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newPlaylist.length) return;
    
    [newPlaylist[index], newPlaylist[targetIndex]] = [newPlaylist[targetIndex], newPlaylist[index]];
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const orderList = newPlaylist.map(item => item.id);
      
      await axios.post(`${BACKEND_URL}/api/playlist/reorder`, orderList, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPlaylist(newPlaylist);
      toast.success('Ordem atualizada!');
    } catch (error) {
      toast.error('Erro ao reordenar');
      loadPlaylist();
    }
  };

  const handleTestAudio = (itemId) => {
    // Se já está tocando essa música, para
    if (playingId === itemId && audioTest) {
      audioTest.pause();
      setAudioTest(null);
      setPlayingId(null);
      return;
    }
    
    // Para qualquer áudio anterior
    if (audioTest) {
      audioTest.pause();
    }
    
    // Toca nova música
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const audio = new Audio(`${BACKEND_URL}/api/playlist/stream/${itemId}`);
    audio.play().catch(err => {
      toast.error('Erro ao reproduzir');
      console.error(err);
    });
    
    // Quando terminar, limpa estado
    audio.onended = () => {
      setPlayingId(null);
      setAudioTest(null);
    };
    
    setAudioTest(audio);
    setPlayingId(itemId);
  };

  const stopTestAudio = () => {
    if (audioTest) {
      audioTest.pause();
      setAudioTest(null);
      setPlayingId(null);
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setFormData({ title: item.title, active: item.active, file: null });
    setDialogOpen(true);
  };

  if (loading) {
    return <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="playlist-management">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">🎵 Playlist Musical</h2>
          <p className="text-gray-600 text-sm">Gerencie as músicas de fundo do site</p>
        </div>
        <Button 
          data-testid="add-music-button"
          onClick={() => { 
            setEditing(null); 
            setFormData({ title: '', file: null }); 
            setDialogOpen(true); 
          }} 
          className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto"
        >
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Música
        </Button>
      </div>

      {playlist.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12 text-gray-500">
            <Music className="mx-auto h-12 w-12 mb-4 opacity-50" />
            <p>Nenhuma música na playlist</p>
            <p className="text-sm mt-2">Adicione músicas para reprodução automática</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tabela Desktop */}
          <div className="hidden md:block">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="text-center p-3 font-semibold">#</th>
                        <th className="text-left p-3 font-semibold">Título</th>
                        <th className="text-left p-3 font-semibold">Arquivo</th>
                        <th className="text-center p-3 font-semibold">Status</th>
                        <th className="text-center p-3 font-semibold">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {playlist.map((item, index) => (
                        <tr key={item.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-center">
                            <div className="flex flex-col gap-1 items-center">
                              <span className="font-bold text-gray-600">#{index + 1}</span>
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" onClick={() => handleMove(index, 'up')} disabled={index === 0} className="h-5 w-5 p-0">
                                  <MoveUp className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleMove(index, 'down')} disabled={index === playlist.length - 1} className="h-5 w-5 p-0">
                                  <MoveDown className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </td>
                          <td className="p-3 font-medium">{item.title}</td>
                          <td className="p-3 text-xs text-gray-600 font-mono">{item.file_name}</td>
                          <td className="p-3 text-center">
                            <Badge variant={item.active ? 'default' : 'secondary'}>
                              {item.active ? 'Ativa' : 'Inativa'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1 justify-center">
                              <Button 
                                size="sm" 
                                variant={playingId === item.id ? 'default' : 'outline'}
                                onClick={() => playingId === item.id ? stopTestAudio() : handleTestAudio(item.id)}
                                className="h-7 text-xs"
                              >
                                {playingId === item.id ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                              </Button>
                              <Button onClick={() => handleEdit(item)} variant="outline" size="sm" className="h-7 text-xs">
                                <Edit className="mr-1 h-3 w-3" />
                                Editar
                              </Button>
                              <Button onClick={() => handleDelete(item.id)} variant="destructive" size="sm" className="h-7 text-xs">
                                <Trash2 className="mr-1 h-3 w-3" />
                                Excluir
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cards Mobile */}
          <div className="md:hidden space-y-3">
            {playlist.map((item, index) => (
              <Card key={item.id} className={!item.active ? 'opacity-60' : ''}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-gray-600">#{index + 1}</span>
                      <Music className="h-4 w-4 text-purple-600" />
                      <h3 className="font-bold text-sm">{item.title}</h3>
                    </div>
                    <Badge variant={item.active ? 'default' : 'secondary'} className="text-xs">
                      {item.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </div>
                  
                  <p className="text-xs text-gray-600 font-mono truncate">{item.file_name}</p>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant={playingId === item.id ? 'default' : 'outline'}
                      onClick={() => playingId === item.id ? stopTestAudio() : handleTestAudio(item.id)}
                      className="flex-1"
                    >
                      {playingId === item.id ? (
                        <><Square className="mr-1 h-3 w-3" /> Parar</>
                      ) : (
                        <><Play className="mr-1 h-3 w-3" /> Testar</>
                      )}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleMove(index, 'up')} disabled={index === 0}>
                      <MoveUp className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleMove(index, 'down')} disabled={index === playlist.length - 1}>
                      <MoveDown className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={() => handleEdit(item)} variant="outline" className="flex-1" size="sm">
                      <Edit className="mr-1 h-3 w-3" />
                      Editar
                    </Button>
                    <Button onClick={() => handleDelete(item.id)} variant="destructive" className="flex-1" size="sm">
                      <Trash2 className="mr-1 h-3 w-3" />
                      Excluir
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="playlist-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Música' : 'Adicionar Música'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Altere os dados da música' : 'Preencha os dados da nova música'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Título da Música</Label>
              <Input
                data-testid="music-title-input"
                placeholder="Ex: Música Ambiente 1"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            {!editing && (
              <div className="space-y-2">
                <Label>Arquivo MP3</Label>
                <Input
                  data-testid="music-file-input"
                  type="file"
                  accept=".mp3,audio/mp3,audio/mpeg"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  required
                  className="cursor-pointer"
                />
                <p className="text-xs text-gray-500">
                  ✓ Apenas arquivos .mp3 | Máximo 50MB
                </p>
                {formData.file && (
                  <p className="text-sm text-green-600">
                    ✓ Arquivo selecionado: {formData.file.name}
                  </p>
                )}
              </div>
            )}
            {editing && (
              <div className="space-y-2">
                <Label>Arquivo Atual</Label>
                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                  {editing.file_name}
                </p>
                <p className="text-xs text-gray-500">
                  Para alterar o arquivo, delete e crie novamente
                </p>
              </div>
            )}
            {editing && (
              <div className="flex items-center space-x-2">
                <Switch
                  data-testid="music-active-switch"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
                <Label>Música ativa</Label>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button data-testid="save-music-button" type="submit" className="bg-purple-600 hover:bg-purple-700">
                {editing ? 'Salvar Alterações' : 'Adicionar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Delete */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja remover esta música da playlist?
          </p>
          <p className="text-xs text-red-600 mt-2">
            ⚠️ O arquivo MP3 será excluído do servidor permanentemente.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlaylistManagement;
